import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Search, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient } from '~/lib/api';
import { formatDateTime } from '~/lib/utils';

export default function CommentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('all');
  const [searchFilter, setSearchFilter] = useState('');
  const queryClient = useQueryClient();

  // Get filter from URL query params
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setSearchFilter(filterParam);
      // Clear the filter param from URL after reading
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-comments', page, status],
    queryFn: () =>
      apiClient.getComments({
        page,
        limit: 20,
        status: status === 'all' ? undefined : status,
      }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ commentId, newStatus }: { commentId: string; newStatus: 'approved' | 'rejected' }) =>
      apiClient.updateCommentStatus(commentId, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-comments'] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant='default'>Approved</Badge>;
      case 'rejected':
        return <Badge variant='destructive'>Rejected</Badge>;
      case 'pending':
        return <Badge variant='secondary'>Pending</Badge>;
      default:
        return <Badge variant='outline'>{status}</Badge>;
    }
  };

  // Filter comments based on search
  const filteredComments = data?.data?.items.filter((comment: any) => {
    if (!searchFilter) return true;
    const searchLower = searchFilter.toLowerCase();
    return (
      comment.id.toLowerCase().includes(searchLower) ||
      comment.content.toLowerCase().includes(searchLower) ||
      comment.user?.username?.toLowerCase().includes(searchLower) ||
      comment.user?.displayName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Comments Moderation</h1>
          <p className='text-muted-foreground'>Review and moderate user comments</p>
        </div>
        <div className='flex items-center gap-2'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              placeholder='Search comments...'
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className='pl-9 w-[250px]'
            />
            {searchFilter && (
              <Button
                variant='ghost'
                size='sm'
                className='absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0'
                onClick={() => setSearchFilter('')}
              >
                <X className='h-4 w-4' />
              </Button>
            )}
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className='w-[180px]'>
              <SelectValue placeholder='Filter by status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value='pending'>Pending</SelectItem>
              <SelectItem value='approved'>Approved</SelectItem>
              <SelectItem value='rejected'>Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Comments List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
              <p className='text-muted-foreground'>Loading comments...</p>
            </div>
          ) : filteredComments?.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>
                {searchFilter ? 'No comments match your search' : 'No comments found'}
              </p>
              {searchFilter && (
                <Button variant='link' onClick={() => setSearchFilter('')} className='mt-2'>
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Video</TableHead>
                    <TableHead>Comment</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredComments?.map((comment: any) => (
                    <TableRow key={comment.id}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {comment.user.avatarUrl ? (
                            <img
                              src={comment.user.avatarUrl}
                              alt={comment.user.username || comment.user.displayName}
                              className='h-8 w-8 rounded-full'
                            />
                          ) : (
                            <div className='h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold'>
                              {(comment.user.username || comment.user.displayName)?.[0]?.toUpperCase()}
                            </div>
                          )}
                          <span className='font-medium'>{comment.user.username || comment.user.displayName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className='font-medium text-sm'>{comment.video.dvdId}</div>
                          <div className='text-xs text-muted-foreground truncate max-w-[200px]'>
                            {comment.video.title}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='max-w-[300px] truncate' title={comment.content}>
                          {comment.content}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1'>
                          <span className='text-yellow-500'>★</span>
                          <span>{comment.rating}/10</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(comment.status)}</TableCell>
                      <TableCell>
                        <div className='text-sm text-muted-foreground'>{formatDateTime(comment.createdAt)}</div>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {comment.status !== 'approved' && (
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() =>
                                updateStatusMutation.mutate({ commentId: comment.id, newStatus: 'approved' })
                              }
                              disabled={updateStatusMutation.isPending}
                            >
                              <CheckCircle2 className='h-4 w-4 mr-1 text-green-600' />
                              Approve
                            </Button>
                          )}
                          {comment.status !== 'rejected' && (
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={() =>
                                updateStatusMutation.mutate({ commentId: comment.id, newStatus: 'rejected' })
                              }
                              disabled={updateStatusMutation.isPending}
                            >
                              <XCircle className='h-4 w-4 mr-1 text-red-600' />
                              Reject
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <div className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, data.data.total)} of {data.data.total} comments
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button variant='outline' size='sm' onClick={() => setPage(page - 1)} disabled={page === 1}>
                      Previous
                    </Button>
                    <div className='text-sm'>
                      Page {page} of {data.data.totalPages}
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage(page + 1)}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
